import type { ReactNode } from 'react';
import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '@repo/shared/common';
import {
  OnboardingProgress,
  ONBOARDING_STEPS,
} from '~/components/onboarding/OnboardingProgress';
import {
  ONBOARDING_VERIFY_LOTTIE,
  ONBOARDING_SUCCESS_LOTTIE,
} from '~/components/onboarding/lottie-assets';
import styles from './onboarding.module.css';

/**
 * Onboarding shell — the branded frame shared by the verify-email and
 * create-organization steps. Mirrors the sign-up split (the surface the user
 * just came from) for a seamless hand-off: a deep Ink aside carrying the brand
 * lockup + a step-progress ledger for orientation, and a width-constrained,
 * centred content pane for the active step. Without this layout the steps
 * rendered full-bleed against the raw `_authed` Outlet.
 *
 * `head()` prefetches both onboarding dotLottie files so they're warm in the
 * HTTP cache before their render points — the verify mark on the first step, and
 * the success checkmark on the post-onboarding landing modal (a different route,
 * reached a beat later, so the prefetch has ample time to complete).
 */
export const Route = createFileRoute('/_authed/onboarding')({
  head: () => ({
    links: [
      { rel: 'prefetch', href: ONBOARDING_VERIFY_LOTTIE },
      { rel: 'prefetch', href: ONBOARDING_SUCCESS_LOTTIE },
    ],
  }),
  component: OnboardingLayout,
});

function OnboardingLayout(): ReactNode {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The onboarding routes drive the step index. Both organization-flavoured
  // steps (choose a pending invitation, or create your own org) sit on the
  // second "organization" step; anything else is the (first) verify-email step.
  const current =
    pathname.includes('create-organization') || pathname.includes('invitations') ? 1 : 0;
  const activeLabel = t(ONBOARDING_STEPS[current]?.labelKey ?? '');

  return (
    <div className={styles.shell}>
      <aside className={styles.aside}>
        <span className={styles.monogram} aria-hidden>
          {APP_NAME.charAt(0)}
        </span>

        <div className={styles.asideInner}>
          <div className={styles.brand}>
            <span className={styles.brandName}>{APP_NAME}</span>
            <span className={styles.brandRule} aria-hidden />
          </div>

          {/* Desktop: the full orientation block (welcome + labelled step ledger). */}
          <div className={styles.asideBody}>
            <div className={styles.intro}>
              <span className={styles.eyebrow}>{t('onboarding.shell.eyebrow')}</span>
              <h2 className={styles.headline}>{t('onboarding.shell.headline')}</h2>
            </div>
            <OnboardingProgress current={current} />
          </div>

          {/* Mobile: a compact progress band under the brand lockup. */}
          <div className={styles.mobileProgress}>
            <div className={styles.mobileBar} aria-hidden>
              {ONBOARDING_STEPS.map((step, index) => (
                <span
                  key={step.key}
                  className={styles.mobileSeg}
                  data-state={index <= current ? 'on' : 'off'}
                />
              ))}
            </div>
            <span className={styles.mobileLabel}>
              {t('onboarding.shell.stepOf', {
                current: current + 1,
                total: ONBOARDING_STEPS.length,
                label: activeLabel,
              })}
            </span>
          </div>
        </div>
      </aside>

      <main className={styles.content}>
        <div className={styles.contentInner}>
          {/* Each onboarding step remounts on navigation and owns its entrance via
              its own <Reveal> cascade — no route-level fade wrapping the Outlet. */}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
